import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';
import rem from 'polished/lib/helpers/rem';

import color from '@decisiv/design-tokens/lib/color';
import spacing from '@decisiv/design-tokens/lib/spacing';

import Avatar from '@decisiv/ui-components/lib/components/Avatar';
import Link from '@decisiv/ui-components/lib/components/Link';
import Tag from '@decisiv/ui-components/lib/components/Tag';
import TopNav from '@decisiv/ui-components/lib/components/TopNav';
import { P } from '@decisiv/ui-components/lib/components/Typography';

// All layout here uses real styled components rather than styled-components'
// `css` prop: managed-care-ui relies on babel-plugin-styled-components for that
// prop, and portal's babel config does not include it, so a `css={...}` would be
// dropped without warning.
const divider = `1px solid ${toColorString(color.opacity.charcoal15)}`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;

  > *:not(:last-child) {
    border-bottom: ${divider};
  }
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(spacing.base)};
`;

// A row-flex wrapper keeps the Tag at its content width; managed-care-ui gets
// the same result with `width: fit-content` on the Tag itself.
const RoleTagRow = styled.div`
  display: flex;
  margin-top: ${rem(spacing.base / 2)};
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  li:not(:last-child) {
    border-bottom: ${divider};
  }

  a {
    display: block;
    /* Matches the padding KeyDS Menu applies to its own items, measured against
       managed-care-ui: 5px 30px 5px 10px. The wide right pad leaves room for the
       submenu affordance. */
    padding: ${rem(spacing.base / 2)} ${rem(spacing.base * 3)}
      ${rem(spacing.base / 2)} ${rem(spacing.base)};
    white-space: nowrap;
    /* managed-care-ui's menu items are plain blue labels, not underlined. */
    text-decoration: none;
  }
`;

/**
 * Logged-in user menu for the Management top nav: avatar plus name, opening to a
 * header block (name, email, role tag) and the menu items.
 *
 * Ported from managed-care-ui's TopNav/UserMenu, with the data coming from
 * `current_user` via props rather than GraphQL, and the links pointing at
 * portal's own profile and logout routes.
 *
 * managed-care-ui builds the item list with the KeyDS `Menu` component, which
 * crashes on the React 16 that portal pins — its `Link` child throws
 * "Cannot read properties of undefined (reading 'length')" inside React's
 * useMemo dependency comparison (ui-components 2.0.1-alpha.226; aligning
 * react-dom with react does not help). KeyDS `Link` on its own is fine, so the
 * items are a plain list of Links styled to match.
 */
function UserMenu({ email, logoutPath, name, profilePath, roleLabel }) {
  const menuItems = useMemo(() => {
    const items = [];

    if (profilePath) {
      items.push({ id: 'my-profile', text: 'My Profile', to: profilePath });
    }

    items.push({ id: 'logout', text: 'Log Out', to: logoutPath });

    return items;
  }, [logoutPath, profilePath]);

  const emblem = (emblemProps) => (
    <Avatar {...emblemProps} email={email} name={name} />
  );

  return (
    <TopNav.Menu emblem={emblem} primaryText={name || email} zIndex={10}>
      <Panel>
        <Identity>
          {name && <P>{name}</P>}
          <P shade={1} size="small">
            {email}
          </P>
          {roleLabel && (
            <RoleTagRow>
              <Tag text={roleLabel} variant="outline" />
            </RoleTagRow>
          )}
        </Identity>
        <ItemList>
          {menuItems.map((item) => (
            <li key={item.id}>
              <Link to={item.to} text={item.text} />
            </li>
          ))}
        </ItemList>
      </Panel>
    </TopNav.Menu>
  );
}

UserMenu.propTypes = {
  email: PropTypes.string.isRequired,
  logoutPath: PropTypes.string.isRequired,
  name: PropTypes.string,
  profilePath: PropTypes.string,
  roleLabel: PropTypes.string,
};

UserMenu.defaultProps = {
  name: undefined,
  profilePath: undefined,
  roleLabel: undefined,
};

export default UserMenu;
